import React from "react";
import { Document, Page, Text, View, Image, Font, Svg, Rect, Path } from "@react-pdf/renderer";
import { styles } from "./style";
import { decrypt } from "../../utils";
import moment from "moment";
import { SafeImage } from "../Form8615Pdf";

Font.register({
  family: "Island Moments",
  fonts: [{ src: "/assets/fonts/IslandMoments-Regular.ttf" }],
});

// ─── Code colours ───
const CODE_COLORS = {
  0: "#22C55E",
  1: "#14B8A6",
  2: "#6366F1",
  3: "#F59E0B",
  4: "#EF4444",
};
const CODE_LABELS = {
  0: "Independent",
  1: "Supervision",
  2: "Limited Assistance",
  3: "Extensive Assistance",
  4: "Totally Dependent",
};

// ─── Helpers ───
const fmt = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");
const fmtLong = (d) => (d ? moment(d).format("MMMM D, YYYY") : "—");
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getAge = (dob) => {
  if (!dob) return "";
  const years = moment().diff(moment(dob), "years");
  return `(${years} years)`;
};

// ─── Unique staff from completed tasks ───
const getContributingStaff = (tasks) => {
  const seen = new Set();
  const staff = [];
  tasks?.forEach((t) => {
    const s = t?.startedBy;
    if (s && !seen.has(s._id)) {
      seen.add(s._id);
      staff.push({ name: s.name || "Staff", role: s.role || "Caregiver" });
    }
  });
  return staff;
};

// ──────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────
export const DailyTaskReportPdf = ({
  data = [],
  dateRange,
  allSignature = [],
  imageMap = {},
  clientInfo,
  facilityInfo,
  adlSnapshot,
  shiftName,
  reportedClients = [],
  facilityLogoUrl,
  servicePlanFieldIds = null,
}) => {
  const facilityName = facilityInfo?.name || data?.[0]?.facility?.name || "";

  // Resolve facility logo
  const facilityLogo = (() => {
    if (facilityLogoUrl) return facilityLogoUrl;
    const photo = facilityInfo?.photo;
    if (!photo) return null;
    try {
      const decrypted = decrypt(photo);
      return decrypted || photo;
    } catch {
      return photo;
    }
  })();

  // Resolve client name — try encrypted first, then plain
  const resolveClientName = (info) => {
    if (!info) return "";
    try {
      const decrypted = decrypt(info.name);
      if (decrypted && typeof decrypted === "string") return decrypted;
      if (typeof decrypted === "object" && decrypted?.firstName)
        return `${decrypted.firstName} ${decrypted.lastName || ""}`.trim();
    } catch {}
    if (typeof info.name === "string") return info.name;
    return "";
  };

  const clientName = resolveClientName(clientInfo) ||
    (data?.[0]?.client?.name ? (() => { try { return decrypt(data[0].client.name); } catch { return data[0].client.name; } })() : "");

  const form1 = clientInfo?.form1 ? decrypt(clientInfo.form1) : null;
  const dob = form1?.dob || clientInfo?.dob || "";
  const sex = form1?.sex || "";
  const height = form1?.height || "";
  const weight = form1?.weight || "";
  const admissionDate = form1?.admissiondate || clientInfo?.admissionDate || "";
  const levelOfCare = form1?.levelofneed || "";

  const reportPeriod = `${fmt(dateRange?.start)} – ${fmt(dateRange?.end)}`;
  const reportDate = fmtLong(new Date());
  const hasAdl = adlSnapshot && adlSnapshot.length > 0;
  const isSingleClient = reportedClients.length === 1;
  const reportType = hasAdl ? "Daily Task Report & ADL Matrix" : "Daily Task Report";

  const contributingStaff = getContributingStaff(data);
  const totalPages = 1 + Math.ceil(data.length / 2); // rough estimate

  // ─── Determine the day for ADL snapshot ───
  const taskDate =
    data?.[0]?.dateTime || dateRange?.start || new Date();
  const dayIdx = new Date(taskDate).getDay();
  const dayName = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ][dayIdx];
  const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  // Filter ADL to only today's scheduled activities
  const todayAdl = hasAdl
    ? adlSnapshot.filter((a) => a.days?.[DAY_KEYS[dayIdx]])
    : [];

  return (
    <Document>
      {/* ═══════ COVER PAGE ═══════ */}
      <Page size="A4" style={styles.coverPage}>
        {/* HEADER BAR */}
        <View style={styles.coverHeader}>
          {/* Business logo or initials */}
          <View style={styles.coverLogo}>
            {facilityLogo ? (
              <Image src={facilityLogo} style={styles.coverLogoIcon} />
            ) : (
              <View style={styles.coverLogoFallback}>
                <Text style={styles.coverLogoFallbackText}>
                  {getInitials(facilityName)}
                </Text>
              </View>
            )}
          </View>
          <View>
            <Text style={styles.coverGenerated}>Generated using Ogiso EHR</Text>
            <Text style={styles.coverGeneratedDate}>
              {moment().format("MM/DD/YYYY [at] h:mm A")}
            </Text>
          </View>
        </View>

        {/* HERO */}
        <View style={styles.coverHero}>
          <View style={styles.coverBadgeRow}>
            <Text style={styles.coverBadge}>Daily Task Report</Text>
          </View>
          <Text style={styles.coverClientName}>{facilityName}</Text>
        </View>

        {/* CLIENT INFORMATION — only when single client selected */}
        {isSingleClient && (
          <View style={styles.infoSection}>
            <View style={styles.infoColumn}>
              <Text style={styles.infoTitle}>Client Information</Text>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Full name</Text>
                <Text style={styles.infoValue}>{clientName}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Date of birth</Text>
                <Text style={styles.infoValue}>
                  {fmt(dob)} {getAge(dob)}
                </Text>
              </View>
              {sex ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Sex</Text>
                  <Text style={styles.infoValue}>{sex}</Text>
                </View>
              ) : null}
              {height || weight ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Height / Weight</Text>
                  <Text style={styles.infoValue}>
                    {height ? `${height}"` : "—"} / {weight ? `${weight} lbs` : "—"}
                  </Text>
                </View>
              ) : null}
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Admission date</Text>
                <Text style={styles.infoValue}>{fmt(admissionDate)}</Text>
              </View>
              {levelOfCare ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Level of care</Text>
                  <Text style={styles.infoValue}>{levelOfCare}</Text>
                </View>
              ) : null}
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Facility</Text>
                <Text style={styles.infoValue}>{facilityName}</Text>
              </View>
            </View>

            <View style={styles.infoColumn}>
              <Text style={styles.infoTitle}>Report Details</Text>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report type</Text>
                <Text style={styles.infoValue}>{reportType}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report date</Text>
                <Text style={styles.infoValue}>{reportDate}</Text>
              </View>
              {shiftName ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Shift</Text>
                  <Text style={styles.infoValue}>{shiftName}</Text>
                </View>
              ) : null}
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report period</Text>
                <Text style={styles.infoValue}>{reportPeriod}</Text>
              </View>
            </View>
          </View>
        )}

        {/* REPORT DETAILS — when multiple clients (no client info section) */}
        {!isSingleClient && (
          <View style={[styles.infoSection, { justifyContent: "flex-start" }]}>
            <View style={styles.infoColumn}>
              <Text style={styles.infoTitle}>Report Details</Text>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report type</Text>
                <Text style={styles.infoValue}>{reportType}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report date</Text>
                <Text style={styles.infoValue}>{reportDate}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Report period</Text>
                <Text style={styles.infoValue}>{reportPeriod}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Facility</Text>
                <Text style={styles.infoValue}>{facilityName}</Text>
              </View>
            </View>
          </View>
        )}

        {/* CONTRIBUTING STAFF */}
        {contributingStaff.length > 0 && (
          <View style={styles.staffSection}>
            <Text style={styles.staffTitle}>Contributing Staff</Text>
            <View style={styles.staffRow}>
              {contributingStaff.map((s, i) => (
                <View key={i} style={styles.staffCard}>
                  <View style={styles.staffAvatar}>
                    <Text style={styles.staffInitials}>
                      {getInitials(s.name)}
                    </Text>
                  </View>
                  <View>
                    <Text style={styles.staffName}>{s.name}</Text>
                    <Text style={styles.staffRole}>{s.role}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* CLIENTS REPORTED ON */}
        {reportedClients.length > 0 && (
          <View style={styles.staffSection}>
            <Text style={styles.staffTitle}>Clients Reported On</Text>
            <View style={styles.staffRow}>
              {reportedClients.map((c, i) => {
                const cName = resolveClientName(c);
                const cForm1 = c?.form1 ? (() => { try { return decrypt(c.form1); } catch { return null; } })() : null;
                const cDob = cForm1?.dob || c?.dob || "";
                return (
                  <View key={i} style={styles.staffCard}>
                    <View style={[styles.staffAvatar, { backgroundColor: "#6366F1" }]}>
                      <Text style={[styles.staffInitials, { color: "#FFF" }]}>
                        {getInitials(cName)}
                      </Text>
                    </View>
                    <View>
                      <Text style={styles.staffName}>{cName}</Text>
                      {cDob ? (
                        <Text style={styles.staffRole}>DOB: {fmt(cDob)}</Text>
                      ) : null}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* ADL INSTRUCTIONS — only for single client */}
        {hasAdl && isSingleClient && (
          <View style={styles.adlSection}>
            <Text style={styles.adlTitle}>ADL Instructions</Text>
            <View style={styles.adlCardsRow}>
              {todayAdl.map((act, i) => (
                <View key={i} style={styles.adlCard}>
                  <Text style={styles.adlCardActivity}>
                    {(act.activity || "").charAt(0).toUpperCase() +
                      (act.activity || "").slice(1)}
                  </Text>
                  <View style={styles.adlCodeRow}>
                    <View
                      style={[
                        styles.adlCodeBadge,
                        { backgroundColor: CODE_COLORS[act.code] || "#6B7280" },
                      ]}
                    >
                      <Text style={styles.adlCodeText}>{act.code ?? 0}</Text>
                    </View>
                    <Text style={styles.adlCodeLabel}>
                      {CODE_LABELS[act.code] || "Independent"}
                    </Text>
                  </View>
                  {act.notes ? (
                    <Text style={styles.adlCardNote}>{act.notes}</Text>
                  ) : null}
                </View>
              ))}
            </View>

            {/* Legend */}
            <View style={styles.adlLegend}>
              <Text style={{ fontSize: 7, color: "#6B7280", marginRight: 4 }}>
                Code legend:
              </Text>
              {Object.entries(CODE_LABELS).map(([code, label]) => (
                <View key={code} style={styles.adlLegendItem}>
                  <View
                    style={[
                      styles.adlLegendDot,
                      { backgroundColor: CODE_COLORS[code] },
                    ]}
                  >
                    <Text
                      style={{ fontSize: 7, color: "#FFF", fontWeight: "bold" }}
                    >
                      {code}
                    </Text>
                  </View>
                  <Text style={styles.adlLegendText}>{label}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* COVER FOOTER */}
        <View style={styles.coverFooter} fixed>
          <View style={styles.footerLeft}>
            <Text style={styles.footerBrand}>Generated using Ogiso EHR</Text>
            <Text style={styles.footerFacility}>{facilityName}</Text>
            <Text style={styles.footerConfidential}>Confidential</Text>
          </View>
          <View style={styles.footerRight}>
            <Text style={styles.footerPage}>Page 1</Text>
            <Text style={styles.footerContinues}>
              Report continues on next page
            </Text>
          </View>
        </View>
      </Page>

      {/* ═══════ TASK CHECKLIST PAGES ═══════ */}
      <Page size="A4" style={styles.checklistPage} wrap>
        {/* Page header */}
        <View style={styles.checklistHeader} fixed>
          <View style={styles.checklistHeaderLeft}>
            {facilityLogo ? (
              <Image src={facilityLogo} style={styles.checklistLogo} />
            ) : (
              <View style={styles.coverLogoFallback}>
                <Text style={styles.coverLogoFallbackText}>
                  {getInitials(facilityName)}
                </Text>
              </View>
            )}
            <View>
              <Text style={styles.checklistFacilityName}>{facilityName}</Text>
              <Text style={styles.checklistFacilitySub}>
                Daily Task Report
              </Text>
            </View>
          </View>
          <View style={styles.checklistHeaderRight}>
            <Text style={styles.checklistClientName}>{clientName}</Text>
            <Text style={styles.checklistDateRange}>{reportPeriod}</Text>
          </View>
        </View>

        <Text style={styles.checklistSectionTitle}>Task Checklist</Text>

        {/* TASK ENTRY CARDS */}
        {data?.map((task, taskIdx) => {
          const sig = allSignature?.find(
            (s) =>
              (s?.staff && s.staff?.id === task?.startedBy?._id) ||
              (s?.admin && s.admin?.id === task?.startedBy?._id),
          );
          const taskClientName = task?.client?.name
            ? decrypt(task.client.name)
            : "N/A";

          return (
            <View key={taskIdx} style={styles.taskCard} wrap={false}>
              {/* Dark header bar */}
              <View style={styles.taskCardHeader}>
                <Text style={styles.taskCardClientName}>{taskClientName}</Text>
                <Text style={styles.taskCardDate}>
                  {fmt(task?.dateTime)}
                </Text>
              </View>

              {/* Category columns */}
              <View style={styles.taskCardBody}>
                {task?.taskType?.subTasks?.map((subTask, subIdx) => {
                  // Only show fields that were actually checked on this task
                  const checkedFieldIds = new Set(
                    (task?.fieldsDone || []).map((fd) => fd?.field?._id).filter(Boolean)
                  );
                  const applicableFields = subTask?.fields?.filter((f) => checkedFieldIds.has(f?._id));
                  if (!applicableFields || applicableFields.length === 0) return null;
                  return (
                  <View key={subIdx} style={styles.taskCategory}>
                    <Text style={styles.taskCategoryTitle}>
                      {subTask?.task?.name || "Task"}
                    </Text>
                    {applicableFields?.map((field, fIdx) => {
                      const fieldDone = task?.fieldsDone?.find(
                        (fd) => fd.field?._id === field._id,
                      );
                      const isChecked = !!fieldDone;
                      return (
                        <View key={fIdx}>
                          <View style={styles.taskCheckRow}>
                            {isChecked ? (
                              <Svg width={10} height={10} viewBox="0 0 10 10">
                                <Rect x="0" y="0" width="10" height="10" rx="2" fill="#374151" />
                                <Path d="M2.5 5.5 L4.2 7.2 L7.5 3.5" stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
                              </Svg>
                            ) : (
                              <View style={styles.taskCheckbox} />
                            )}
                            <Text
                              style={[
                                styles.taskFieldName,
                                isChecked && { color: "#C4320A" },
                              ]}
                            >
                              {field?.name || "N/A"}
                            </Text>
                          </View>
                          {fieldDone?.textField && (
                            <Text style={styles.taskFieldValue}>
                              {fieldDone.textField}
                            </Text>
                          )}
                          {fieldDone?.textEntries?.map((te, teIdx) => (
                            <Text key={teIdx} style={styles.taskFieldValue}>
                              {te.text}
                            </Text>
                          ))}
                        </View>
                      );
                    })}
                  </View>
                  );
                })}
              </View>

              {/* Comments */}
              {task?.comments?.length > 0 && (
                <View style={styles.taskCommentsSection}>
                  <Text style={styles.taskCommentsTitle}>Comments</Text>
                  {task.comments.map((c, cIdx) => (
                    <View key={cIdx}>
                      <Text style={styles.taskCommentAuthor}>
                        {c?.staff?.name || "Staff"} ·{" "}
                        {moment(c.date).format("MM/DD/YYYY [at] h:mm A")}
                      </Text>
                      <Text style={styles.taskCommentText}>
                        "{c?.comment || ""}"
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Shift + Staff + Signature */}
              <View style={styles.taskFooter}>
                <Text style={styles.taskShiftLabel}>
                  Shift: {task?.shift?.[0]?.name || "—"}
                </Text>
                <View style={styles.taskStaffRow}>
                  <Text style={styles.taskStaffLabel}>Staff:</Text>
                  <Text style={styles.taskStaffName}>
                    {task?.startedBy?.name || "—"}
                  </Text>
                  {sig?.signature ? (
                    <View style={styles.taskSignature}>
                      <SafeImage
                        src={decrypt(sig.signature) || sig.signature}
                        style={{ width: 50 }}
                        imageMap={imageMap}
                      />
                    </View>
                  ) : sig?.signatureText ? (
                    <View style={styles.taskSignature}>
                      <Text style={styles.taskSigText}>
                        {sig.signatureText}
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.taskSignature} />
                  )}
                </View>
              </View>
            </View>
          );
        })}

        {/* Page footer */}
        <View style={styles.pageFooter} fixed>
          <Text>
            {facilityName} · Confidential — Generated by Ogiso EHR
          </Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default DailyTaskReportPdf;
